import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Building, Users, Activity, TrendingUp, Calendar, Award, Bell } from 'lucide-react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { supabase } from '@/integrations/supabase/client'
import { auditLogger } from '@/utils/auditLogger'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Link } from 'react-router-dom'
import { SystemPushNotificationManager } from '@/components/superadmin/SystemPushNotificationManager'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import TenantReferralMindmap from '@/components/superadmin/TenantReferralMindmap'
import { AdminManagementModule } from '@/components/superadmin/AdminManagementModule'
import { PricingManagementModule } from '@/components/superadmin/PricingManagementModule'
import { StatisticsModule } from '@/components/superadmin/StatisticsModule'
import { SystemSettingsModule } from '@/components/superadmin/SystemSettingsModule'
import { ForestDonationModule } from '@/components/superadmin/ForestDonationModule'
import { SocialProjectsModule } from '@/components/superadmin/SocialProjectsModule'
import { SubscriptionStatusCard } from '@/components/superadmin/SubscriptionStatusCard'
import { WebhookMonitoringModule } from '@/components/superadmin/WebhookMonitoringModule'
import SecurityDashboard from '@/components/superadmin/SecurityDashboard'
import { SubscriptionPlans } from '@/components/subscription/SubscriptionPlans'
import { LandingPagesModule } from '@/components/superadmin/LandingPagesModule'
import { LandingPageTemplatesModule } from '@/components/superadmin/LandingPageTemplatesModule'
import { TenantFeatureManagement } from '@/components/superadmin/TenantFeatureManagement'

interface DashboardStats {
  totalTenants: number
  activeTenants: number
  totalUsers: number
  activeUsers: number
  totalStamps: number
  stampsToday: number
  totalCustomers: number
  monthlyGrowth: number
}

interface RecentTenant {
  id: string
  name: string
  created_at: string
  status: string
  admin_email?: string
}

interface Tenant {
  id: string
  name: string
}

const SuperadminDashboard = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalTenants: 0,
    activeTenants: 0,
    totalUsers: 0,
    activeUsers: 0,
    totalStamps: 0,
    stampsToday: 0,
    totalCustomers: 0,
    monthlyGrowth: 0
  })
  
  const [recentTenants, setRecentTenants] = useState<RecentTenant[]>([])
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      
      // Fetch tenants data
      const { data: tenants, error: tenantsError } = await supabase
        .from('tenants')
        .select('id, name, status, created_at, admin_user_id')
        .order('created_at', { ascending: false })

      if (tenantsError) throw tenantsError

      const totalTenants = tenants?.length || 0
      const activeTenants = tenants?.filter(t => t.status === 'active').length || 0

      // Fetch users data
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, created_at, updated_at')

      if (profilesError) throw profilesError

      const totalUsers = profiles?.length || 0
      
      // Active users (updated in last 30 days)
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
      const activeUsers = profiles?.filter(p => 
        new Date(p.updated_at || p.created_at) > new Date(thirtyDaysAgo)
      ).length || 0

      // Fetch stamps data
      const { data: stamps, error: stampsError } = await supabase
        .from('stamps')
        .select('id, created_at')

      if (stampsError) throw stampsError

      const totalStamps = stamps?.length || 0
      
      // Stamps today
      const today = new Date().toDateString()
      const stampsToday = stamps?.filter(s => 
        new Date(s.created_at).toDateString() === today
      ).length || 0

      // Fetch customers data
      const { data: customers, error: customersError } = await supabase
        .from('customers')
        .select('id, created_at')

      if (customersError) throw customersError

      const totalCustomers = customers?.length || 0

      // Calculate monthly growth (users created this month vs last month)
      const now = new Date()
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1)
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      
      const thisMonthUsers = profiles?.filter(p => 
        new Date(p.created_at) >= thisMonthStart
      ).length || 0
      
      const lastMonthUsers = profiles?.filter(p => {
        const createdAt = new Date(p.created_at)
        return createdAt >= lastMonthStart && createdAt < thisMonthStart
      }).length || 0

      const monthlyGrowth = lastMonthUsers > 0 
        ? Math.round(((thisMonthUsers - lastMonthUsers) / lastMonthUsers) * 100)
        : thisMonthUsers > 0 ? 100 : 0

      setStats({
        totalTenants,
        activeTenants,
        totalUsers,
        activeUsers,
        totalStamps,
        stampsToday,
        totalCustomers,
        monthlyGrowth
      })

      // Get recent tenants with admin emails
      const recentTenantsWithEmails = await Promise.all(
        (tenants?.slice(0, 5) || []).map(async (tenant) => {
          let adminEmail = 'N/A'
          if (tenant.admin_user_id) {
            const { data: profile } = await supabase
              .from('profiles')
              .select('email')
              .eq('user_id', tenant.admin_user_id)
              .maybeSingle()
            
            adminEmail = profile?.email || 'N/A'
          }
          
          return {
            id: tenant.id,
            name: tenant.name,
            created_at: tenant.created_at,
            status: tenant.status,
            admin_email: adminEmail
          }
        })
      )

      setRecentTenants(recentTenantsWithEmails)
      
      // Set all tenants for the notification manager
      setTenants(tenants?.map(t => ({ id: t.id, name: t.name })) || [])
      
      await auditLogger.logDataAccess('superadmin_dashboard', undefined, 'view')
      
    } catch (error: any) {
      console.error('Error fetching dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
    
    if (diffInHours < 1) return 'Vor wenigen Minuten'
    if (diffInHours < 24) return `Vor ${diffInHours} Stunden`
    if (diffInHours < 48) return 'Gestern'
    if (diffInHours < 72) return 'Vor 2 Tagen'
    return `Vor ${Math.floor(diffInHours / 24)} Tagen`
  }

  const getStatusBadge = (status: string) => {
    const variants = {
      active: 'bg-green-100 text-green-800 hover:bg-green-100',
      pending: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100',
      suspended: 'bg-red-100 text-red-800 hover:bg-red-100'
    }
    
    const labels = {
      active: 'Aktiv',
      pending: 'Ausstehend',
      suspended: 'Gesperrt'
    }

    return (
      <Badge className={variants[status as keyof typeof variants] || variants.pending}>
        {labels[status as keyof typeof labels] || status}
      </Badge>
    )
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 md:space-y-6">
        {/* Welcome Header */}
        <div className="px-1 sm:px-0">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Superadmin Dashboard</h2>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Übersicht über alle Unternehmen und Systemmetriken
          </p>
        </div>

        <Tabs defaultValue="overview" className="space-y-4 md:space-y-6">
          {/* Multi-Row Mobile Tabs */}
          <div className="w-full">
            <TabsList className="h-auto min-h-9 sm:min-h-10 flex flex-wrap gap-1 sm:gap-2 bg-muted p-1 rounded-md">
              <TabsTrigger value="overview" className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm min-w-fit">Übersicht</TabsTrigger>
              <TabsTrigger value="admins" className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm min-w-fit">Admins</TabsTrigger>
              <TabsTrigger value="features" className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm min-w-fit">Features</TabsTrigger>
              <TabsTrigger value="pricing" className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm min-w-fit">Preise</TabsTrigger>
              <TabsTrigger value="subscriptions" className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm min-w-fit">Abos</TabsTrigger>
              <TabsTrigger value="landingpages" className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm min-w-fit">Landing</TabsTrigger>
              <TabsTrigger value="templates" className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm min-w-fit">Templates</TabsTrigger>
              <TabsTrigger value="webhooks" className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm min-w-fit">Webhooks</TabsTrigger>
              <TabsTrigger value="security" className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm min-w-fit">Sicherheit</TabsTrigger>
              <TabsTrigger value="statistics" className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm min-w-fit">Statistiken</TabsTrigger>
              <TabsTrigger value="settings" className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm min-w-fit">System</TabsTrigger>
              <TabsTrigger value="forest" className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm min-w-fit">Wald</TabsTrigger>
              <TabsTrigger value="social" className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm min-w-fit">Sozial</TabsTrigger>
              <TabsTrigger value="notifications" className="px-2 sm:px-3 py-1.5 text-xs sm:text-sm min-w-fit">Push</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="overview" className="space-y-4 md:space-y-6">
            {/* Stats Grid - Mobile Optimized */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <Card className="p-3 sm:p-4">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-0 mb-2">
                  <CardTitle className="text-xs sm:text-sm font-medium">
                    Unternehmen
                  </CardTitle>
                  <Building className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600 flex-shrink-0" />
                </CardHeader>
                <CardContent className="p-0">
                  <div className="text-lg sm:text-2xl font-bold">{stats.totalTenants}</div>
                  <p className="text-xs text-muted-foreground">
                    {stats.activeTenants} aktiv
                  </p>
                </CardContent>
              </Card>

              <Card className="p-3 sm:p-4">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-0 mb-2">
                  <CardTitle className="text-xs sm:text-sm font-medium">
                    Benutzer
                  </CardTitle>
                  <Users className="h-3 w-3 sm:h-4 sm:w-4 text-green-600 flex-shrink-0" />
                </CardHeader>
                <CardContent className="p-0">
                  <div className="text-lg sm:text-2xl font-bold">{stats.totalUsers.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground">
                    {stats.activeUsers} aktiv
                  </p>
                </CardContent>
              </Card>

              <Card className="p-3 sm:p-4">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-0 mb-2">
                  <CardTitle className="text-xs sm:text-sm font-medium">
                    Stempel
                  </CardTitle>
                  <Activity className="h-3 w-3 sm:h-4 sm:w-4 text-orange-600 flex-shrink-0" />
                </CardHeader>
                <CardContent className="p-0">
                  <div className="text-lg sm:text-2xl font-bold">{stats.stampsToday}</div>
                  <p className="text-xs text-muted-foreground">
                    heute
                  </p>
                </CardContent>
              </Card>

              <Card className="p-3 sm:p-4">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-0 mb-2">
                  <CardTitle className="text-xs sm:text-sm font-medium">
                    Wachstum
                  </CardTitle>
                  <TrendingUp className={`h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0 ${stats.monthlyGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`} />
                </CardHeader>
                <CardContent className="p-0">
                  <div className="text-lg sm:text-2xl font-bold">
                    {stats.monthlyGrowth > 0 ? '+' : ''}{stats.monthlyGrowth}%
                  </div>
                  <p className="text-xs text-muted-foreground">
                    monatlich
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity - Mobile Stack */}
            <div className="grid gap-4 sm:gap-6 md:grid-cols-3">
              <Card>
                <CardHeader className="pb-3 sm:pb-4">
                  <CardTitle className="text-base sm:text-lg">Neueste Unternehmen</CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    Zuletzt registrierte Unternehmen
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="text-center py-6 sm:py-8 text-muted-foreground text-sm">
                      Lade Daten...
                    </div>
                  ) : recentTenants.length === 0 ? (
                    <div className="text-center py-6 sm:py-8 text-muted-foreground text-sm">
                      Noch keine Unternehmen
                    </div>
                  ) : (
                    <div className="space-y-3 sm:space-y-4">
                      {recentTenants.map((tenant) => (
                        <div key={tenant.id} className="flex items-start sm:items-center justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate text-sm sm:text-base">{tenant.name}</p>
                            <p className="text-xs sm:text-sm text-muted-foreground truncate">
                              {tenant.admin_email}
                            </p>
                            <p className="text-xs text-muted-foreground sm:hidden">
                              {formatDate(tenant.created_at)}
                            </p>
                          </div>
                          <div className="flex flex-col sm:flex-row items-end sm:items-center gap-1 sm:gap-2">
                            {getStatusBadge(tenant.status)}
                            <span className="hidden sm:inline text-xs text-muted-foreground">
                              {formatDate(tenant.created_at)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3 sm:pb-4">
                  <CardTitle className="text-base sm:text-lg">Systemstatus</CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    Aktuelle Systemmetriken
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm sm:text-base">Gesamt Kunden</span>
                      <span className="text-blue-600 font-medium text-sm sm:text-base">{stats.totalCustomers.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm sm:text-base">Aktive Benutzer</span>
                      <span className="text-green-600 font-medium text-sm sm:text-base">{stats.activeUsers}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm sm:text-base">Durchschn. Stempel</span>
                      <span className="text-purple-600 font-medium text-sm sm:text-base">
                        {stats.totalCustomers > 0 ? (stats.totalStamps / stats.totalCustomers).toFixed(1) : '0.0'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm sm:text-base">Aktive Tenants</span>
                      <span className="text-green-600 font-medium text-sm sm:text-base">
                        {Math.round((stats.activeTenants / (stats.totalTenants || 1)) * 100)}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Subscription Status Card */}
              <SubscriptionStatusCard />
            </div>
          </TabsContent>

          <TabsContent value="admins">
            <AdminManagementModule />
          </TabsContent>

          <TabsContent value="features">
            <TenantFeatureManagement />
          </TabsContent>

          <TabsContent value="pricing">
            <PricingManagementModule />
          </TabsContent>

          <TabsContent value="subscriptions">
            <SubscriptionPlans />
          </TabsContent>

          <TabsContent value="landingpages">
            <LandingPagesModule />
          </TabsContent>

          <TabsContent value="templates">
            <LandingPageTemplatesModule />
          </TabsContent>

          <TabsContent value="webhooks">
            <WebhookMonitoringModule />
          </TabsContent>

          <TabsContent value="security">
            <SecurityDashboard />
          </TabsContent>

          <TabsContent value="statistics">
            <StatisticsModule />
          </TabsContent>

          <TabsContent value="settings">
            <SystemSettingsModule />
          </TabsContent>

          <TabsContent value="forest">
            <ForestDonationModule />
          </TabsContent>

          <TabsContent value="social">
            <SocialProjectsModule />
          </TabsContent>

          <TabsContent value="notifications">
            <SystemPushNotificationManager tenants={tenants} />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  )
}

export default SuperadminDashboard